import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportAnalysis.css';

const FixedAssetLedger = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [viewingAsset, setViewingAsset] = useState(null);
  const [entries, setEntries] = useState([
    {
      id: Date.now(),
      assetName: '',
      assetCategory: 'computer',
      purchaseDate: new Date().toISOString().split('T')[0],
      purchaseAmount: '',
      assetLife: '',
      depreciationMethod: 'straight_line',
      depreciationRate: ''
    }
  ]);
  const [expandedEntry, setExpandedEntry] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const db = getDatabase();
      const assetsRef = ref(db, 'HTAMS/expenses/assetledger');
      const snapshot = await get(assetsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const assetsList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setAssets(assetsList);
      } else {
        setAssets([]);
      }
    } catch (error) {
      console.error('Error fetching fixed assets:', error);
      toast.error('Failed to fetch asset data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    
    // Validation for amount and rate fields
    if (name === 'purchaseAmount' || name === 'assetLife' || name === 'depreciationRate') {
      // Only allow numbers and decimal point
      const numericValue = value.replace(/[^\d.]/g, '');
      // Prevent multiple decimal points
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      
      setEntries(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [name]: validValue };
        return updated;
      });
      return;
    }

    setEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  const addNewEntry = () => {
    const newEntry = {
      id: Date.now(),
      assetName: '',
      assetCategory: 'computer',
      purchaseDate: new Date().toISOString().split('T')[0],
      purchaseAmount: '',
      assetLife: '',
      depreciationMethod: 'straight_line',
      depreciationRate: ''
    };
    setEntries([...entries, newEntry]);
    setExpandedEntry(entries.length);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      const updated = entries.filter((_, i) => i !== index);
      setEntries(updated);
      if (expandedEntry >= updated.length) {
        setExpandedEntry(updated.length - 1);
      }
    }
  };

  const toggleEntry = (index) => {
    setExpandedEntry(expandedEntry === index ? -1 : index);
  };

  const validateEntry = (entry, index) => {
    const entryNum = index + 1;
    if (!entry.assetName || !entry.assetName.trim()) {
      toast.error(`Entry ${entryNum}: Asset name is required`);
      return false;
    }
    if (!entry.assetCategory) {
      toast.error(`Entry ${entryNum}: Asset category is required`);
      return false;
    }
    if (!entry.purchaseDate) {
      toast.error(`Entry ${entryNum}: Purchase date is required`);
      return false;
    }
    if (!entry.purchaseAmount || entry.purchaseAmount.trim() === '') {
      toast.error(`Entry ${entryNum}: Purchase amount is required`);
      return false;
    }
    if (parseFloat(entry.purchaseAmount) <= 0) {
      toast.error(`Entry ${entryNum}: Purchase amount must be greater than 0`);
      return false;
    }
    if (!entry.assetLife || entry.assetLife.trim() === '') {
      toast.error(`Entry ${entryNum}: Asset life is required`);
      return false;
    }
    if (parseFloat(entry.assetLife) <= 0) {
      toast.error(`Entry ${entryNum}: Asset life must be greater than 0`);
      return false;
    }
    if (!entry.depreciationRate || parseFloat(entry.depreciationRate) < 0 || parseFloat(entry.depreciationRate) > 100) {
      toast.error(`Entry ${entryNum}: Depreciation rate must be between 0 and 100`);
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setEntries([
      {
        id: Date.now(),
        assetName: '',
        assetCategory: 'computer',
        purchaseDate: new Date().toISOString().split('T')[0],
        purchaseAmount: '',
        assetLife: '',
        depreciationMethod: 'straight_line',
        depreciationRate: ''
      }
    ]);
    setExpandedEntry(0);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all entries
    for (let i = 0; i < entries.length; i++) {
      if (!validateEntry(entries[i], i)) {
        return;
      }
    }
    
    setSubmitting(true);

    try {
      const db = getDatabase();
      const assetsRef = ref(db, 'HTAMS/expenses/assetledger');

      if (editingId) {
        // Update single entry
        const assetRef = ref(db, `HTAMS/expenses/assetledger/${editingId}`);
        const { id, ...entryData } = entries[0];
        await update(assetRef, entryData);
        toast.success('Asset updated successfully!');
      } else {
        // Create multiple entries
        const savePromises = entries.map(entry => {
          const { id, ...entryData } = entry;
          const newAssetRef = push(assetsRef);
          return set(newAssetRef, entryData);
        });
        await Promise.all(savePromises);
        toast.success(`${entries.length} Asset${entries.length > 1 ? 's' : ''} added successfully!`);
      }

      resetForm();
      fetchAssets();
    } catch (error) {
      console.error('Error saving asset:', error);
      toast.error('Failed to save asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (asset) => {
    setEntries([
      {
        id: Date.now(),
        assetName: asset.assetName,
        assetCategory: asset.assetCategory,
        purchaseDate: asset.purchaseDate,
        purchaseAmount: asset.purchaseAmount,
        assetLife: asset.assetLife,
        depreciationMethod: asset.depreciationMethod,
        depreciationRate: asset.depreciationRate
      }
    ]);
    setExpandedEntry(0);
    setEditingId(asset.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete asset "${name}"?`)) {
      try {
        const db = getDatabase();
        const assetRef = ref(db, `HTAMS/expenses/assetledger/${id}`);
        await remove(assetRef);
        toast.success('Asset deleted successfully!');
        fetchAssets();
      } catch (error) {
        console.error('Error deleting asset:', error);
        toast.error('Failed to delete asset');
      }
    }
  };

  const handleView = (asset) => {
    setViewingAsset(asset);
  };

  const closeViewModal = () => {
    setViewingAsset(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.assetCategory?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = (!dateFilter.start || new Date(asset.purchaseDate) >= new Date(dateFilter.start)) &&
                       (!dateFilter.end || new Date(asset.purchaseDate) <= new Date(dateFilter.end));
    return matchesSearch && matchesDate;
  });

  const totalValue = filteredAssets.reduce((sum, asset) => sum + (parseFloat(asset.purchaseAmount) || 0), 0);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAssets = filteredAssets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const exportToExcel = () => {
    if (filteredAssets.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Prepare data for export
    const exportData = filteredAssets.map((asset, index) => ({
      'Sr No': index + 1,
      'Asset Name': asset.assetName || '-',
      'Category': asset.assetCategory || '-',
      'Purchase Date': formatDate(asset.purchaseDate),
      'Purchase Amount': asset.purchaseAmount,
      'Asset Life (Years)': asset.assetLife,
      'Depreciation Method': asset.depreciationMethod?.replace(/_/g, ' ') || '-',
      'Depreciation Rate (%)': asset.depreciationRate
    }));

    // Convert to CSV
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Fixed_Asset_Ledger_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredAssets.length} entries successfully!`);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter]);

  if (loading) return <div className="loading">Loading fixed assets...</div>;

  return (
    <div className="report-analysis-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="page-header">
        <h2>Fixed Asset Ledger</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="add-btn"
            onClick={exportToExcel}
            style={{ background: '#28a745' }}
          >
            📥 Export to Excel
          </button>
          <button 
            className="add-btn"
            onClick={() => {
              if (showForm && editingId) {
                handleCancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? '✕ Close' : '+ Add Asset'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3 className="form-title">{editingId ? 'Edit Fixed Asset' : 'Add New Fixed Asset'}</h3>
          <form onSubmit={handleSubmit} className="purchase-form">
            {entries.map((entry, index) => (
              <div key={entry.id} style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                <div 
                  style={{
                    background: expandedEntry === index ? '#2c5f2d' : '#f8f9fa',
                    color: expandedEntry === index ? 'white' : '#333',
                    padding: '12px 15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontWeight: '500'
                  }}
                >
                  <span 
                    onClick={() => toggleEntry(index)}
                    style={{ cursor: 'pointer', flex: 1 }}
                  >
                    Entry {index + 1} {entry.assetName && `- ${entry.assetName}`} {entry.purchaseAmount && `(₹${entry.purchaseAmount})`}
                  </span>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {!editingId && entries.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEntry(index);
                        }}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        Remove
                      </button>
                    )}
                    <span 
                      onClick={() => toggleEntry(index)}
                      style={{ cursor: 'pointer', fontSize: '20px' }}
                    >
                      {expandedEntry === index ? '−' : '+'}
                    </span>
                  </div>
                </div>
                
                {expandedEntry === index && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(5, 1fr)', 
                      gap: '15px',
                      marginBottom: '15px'
                    }}>
                      <div className="form-group">
                        <label>Asset Name <span className="required">*</span></label>
                        <input
                          type="text"
                          name="assetName"
                          value={entry.assetName}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter asset name"
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Asset Category <span className="required">*</span></label>
                        <select
                          name="assetCategory"
                          value={entry.assetCategory}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                          className="form-control"
                        >
                          <option value="computer">Computer</option>
                          <option value="machinery">Machinery</option>
                          <option value="furniture">Furniture</option>
                          <option value="vehicle">Vehicle</option>
                          <option value="equipment">Equipment</option>
                          <option value="building">Building</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Purchase Date <span className="required">*</span></label>
                        <input
                          type="date"
                          name="purchaseDate"
                          value={entry.purchaseDate}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Purchase Amount <span className="required">*</span></label>
                        <input
                          type="number"
                          name="purchaseAmount"
                          value={entry.purchaseAmount}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter purchase amount"
                          required
                          step="0.01"
                          min="0"
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Asset Life (years) <span className="required">*</span></label>
                        <input
                          type="number"
                          name="assetLife"
                          value={entry.assetLife}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter asset life in years"
                          required
                          min="1"
                          className="form-control"
                        />
                      </div>

                      <div className="form-group">
                        <label>Depreciation Method <span className="required">*</span></label>
                        <select
                          name="depreciationMethod"
                          value={entry.depreciationMethod}
                          onChange={(e) => handleInputChange(e, index)}
                          required
                          className="form-control"
                        >
                          <option value="straight_line">Straight Line</option>
                          <option value="declining_balance">Declining Balance</option>
                          <option value="double_declining">Double Declining</option>
                          <option value="sum_of_years">Sum of Years Digits</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Depreciation Rate (%) <span className="required">*</span></label>
                        <input
                          type="number"
                          name="depreciationRate"
                          value={entry.depreciationRate}
                          onChange={(e) => handleInputChange(e, index)}
                          placeholder="Enter depreciation rate"
                          required
                          step="0.01"
                          min="0"
                          max="100"
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {!editingId && (
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={addNewEntry}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    display: 'inline-block'
                  }}
                >
                  + Add Another Entry
                </button>
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleCancelEdit}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="btn-submit"
              >
                {submitting ? 'Saving...' : (editingId ? 'Update Asset' : `Save ${entries.length} Asset${entries.length > 1 ? 's' : ''}`)}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by asset name or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flex: '2' }}
        />
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
          <input
            type="date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
            className="date-input"
          />
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
          <input
            type="date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
            className="date-input"
          />
        </div>
        
        {(searchTerm || dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setDateFilter({ start: '', end: '' });
            }}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Asset Value</h3>
          <p>₹{totalValue.toLocaleString()}</p>
        </div>
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Asset Name</th>
              <th>Category</th>
              <th>Purchase Date</th>
              <th>Purchase Amount</th>
              <th>Asset Life</th>
              <th>Depreciation Rate</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentAssets.length > 0 ? (
              currentAssets.map((asset, index) => (
                <tr key={asset.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{asset.assetName}</td>
                  <td>{asset.assetCategory}</td>
                  <td>{formatDate(asset.purchaseDate)}</td>
                  <td>₹{parseFloat(asset.purchaseAmount)?.toLocaleString()}</td>
                  <td>{asset.assetLife} years</td>
                  <td>{asset.depreciationRate}%</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view-btn"
                        onClick={() => handleView(asset)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(asset)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(asset.id, asset.assetName)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>
        
        {currentAssets.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🏢</div>
            <h3>No fixed assets found</h3>
            <p>Add fixed assets using the "Add Asset" button above.</p>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewingAsset && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Fixed Asset Details</h3>
              <button className="modal-close" onClick={closeViewModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Asset Name:</label>
                  <span>{viewingAsset.assetName}</span>
                </div>
                <div className="detail-item">
                  <label>Category:</label>
                  <span>{viewingAsset.assetCategory}</span>
                </div>
                <div className="detail-item">
                  <label>Purchase Date:</label>
                  <span>{formatDate(viewingAsset.purchaseDate)}</span>
                </div>
                <div className="detail-item">
                  <label>Purchase Amount:</label>
                  <span className="highlight-amount">₹{parseFloat(viewingAsset.purchaseAmount)?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Asset Life:</label>
                  <span>{viewingAsset.assetLife} years</span>
                </div>
                <div className="detail-item">
                  <label>Depreciation Method:</label>
                  <span>{viewingAsset.depreciationMethod?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                </div>
                <div className="detail-item">
                  <label>Depreciation Rate:</label>
                  <span>{viewingAsset.depreciationRate}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredAssets.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredAssets.length)} of {filteredAssets.length} entries
          </div>
          <div className="pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            {getPageNumbers().map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
              >
                {number}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FixedAssetLedger;
